<template>
  <PCHeaderBar />
  
  <div class="pc__new-arrival">
    <div class="pc__new-arrival-inner">
      <PCBreadcrumbs
        :breadcrumbs="[
          { id: '/', name: 'Home', path: '/' },
          { id: '/flash-sales', name: 'New Arrival' }
        ]"
        :breadcrumbs:click="(e: IBreadcrumbs) => e.path && $router.push(e.path)"
      />

      <h1 class="header-title">New Arrival</h1>

      <div class="tabs-wrapper">
        <div class="tabs" id="pc__new-arrival__tabs" @wheel="handleCategoryScroll">
          <div
            v-for="(ctg, idx) in newCategories"
            :key="ctg.key"
            :id="`pc__new-arrival-tab__${ctg.key}`"
            :select="selectKey === ctg.key ? 1 : 0"
            class="tab-item"
            @click="handleTabClick(idx, ctg)"
          >
            {{ ctg.name }}
          </div>
        </div>

        <div class="more-ctg-btn" @click="handleShowAllCtgClick">
          Expand <van-icon name="arrow-down" />
        </div>
        <div class="ctg-all-card" v-show="showAll">
          <div class="header-box">
            <span>All Categories</span>
            <div class="collapse-btn" @click="handleShowAllCtgClick">
              Collapse <van-icon name="arrow-up"  />
            </div>
          </div>
          <div class="inner-ctg-card">
            <div
              v-for="(ctg, idx) in newCategories"
              :key="ctg.key"
              class="ctg-tag"
              :select="ctg.key === selectKey ? 1 : 0"
              @click="handleTabClick(idx, ctg)"
            >{{ ctg.name }}</div>
          </div>
        </div>
      </div>

      <div class="listings-wrapper">
        <van-skeleton :row="16" :loading="loadingStatus === 'refresh'">
          <div class="listings">
            <div
              v-for="item in listings"
              :key="item.listingId"
              class="listing-item"
            >
              <PCProductItemCard type="whats-new" :product-data="item" />
            </div>
          </div>
          <template #template>
            <div style="display: flex;flex-wrap: wrap;justify-content: center;width: 100%;">
              <div
                v-for="i in 20"
                :key="i"
                style="width: 20%;padding: 10px;display: flex;flex-direction: column;align-items: center;"
              >
                <div style="height: 0;width:100%;padding-bottom: 100%;position: relative;">
                  <van-skeleton-image style="position: absolute;top: 0;left: 0;width: 100%;height: 100%;" />
                </div>
                <van-skeleton-paragraph />
                <van-skeleton-paragraph />
              </div>
            </div>
          </template>
        </van-skeleton>
      </div>

      <div class="load-more" @click="handleLoadMoreClick" v-if="loadingStatus !== 'over'">
        <span class="loading" v-show="loadingStatus === 'loading' || loadingStatus === 'refresh'">
          <van-loading size="20px" color="#DD3131" type="spinner" />
        </span>
        <span>SHOW MORE</span>
      </div>
    </div>
  </div>

  <PCFooter />
</template>

<script setup lang="ts">
import type { INewArrivalCategory, IListing } from '@/pages/new-arrival.vue'
import type { IBreadcrumbs } from '~/components/PC/Breadcrumbs.vue';

interface IProps {
  listings: IListing[]
  newCategories: INewArrivalCategory[]
  selectKey: string,
  loadingStatus: 'over' | 'loading' | 'refresh' | 'loaded'
}
const props = defineProps<IProps>()
const emits = defineEmits<{
  (e: 'load'): void,
  (e: 'category:change', id: number): void
}>()
const { $throttle } = useNuxtApp()
const handleLoadMoreClick = () => {
  emits('load')
}

const handleTabClick = (e: number, ctg: INewArrivalCategory) => {
  emits('category:change', e)

  closeAllCtg()
}

if(process.client) {
  watchEffect(async() => {
    const actItem = props.selectKey

    if(actItem) {
      await nextTick()
      
      const el = document.getElementById(`pc__new-arrival-tab__${props.selectKey}`) as HTMLDivElement
      const tabsEl = document.getElementById('pc__new-arrival__tabs') as HTMLDivElement

      if(tabsEl && el) {
        const width21 = el.clientWidth / 2
        const tabsW = tabsEl.clientWidth / 2
        
        const offsetLeft = el.offsetLeft + width21 - tabsW
        
        tabsEl.scrollLeft = offsetLeft
      }
    }
  })
}

const closeAllCtg = $throttle(() => {
  showAll.value = false
}, 20)

onMounted(() => {
  window.addEventListener('scroll', closeAllCtg)
})
onUnmounted(() => {
  window.removeEventListener('scroll', closeAllCtg)
})

const handleCategoryScroll = (e: WheelEvent) => {
  e.preventDefault()

  const val = e.deltaY / 2
  const ctgEl = document.getElementsByClassName('tabs')[0]
  ctgEl.scrollLeft += val
}

const showAll = ref<boolean>(false)
const handleShowAllCtgClick = () => {
  showAll.value = !showAll.value
}
</script>

<style lang="scss" scoped>
.pc__new-arrival{
  background-image: url('https://image.kilimall.com/kenya/PcImages/flashsales_header.png.webp');
  background-repeat: no-repeat;
  .pc__new-arrival-inner{
    width: var(--viewport-width);
    margin: 0 auto;
    padding-bottom: 80px;
    .header-title{
      font-size: 52px;
      font-weight: 600;
      padding: 60px;
    }
  }
}
.tabs-wrapper{
  position: relative;
  z-index: 1004;
  .more-ctg-btn{
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 180px;
    @include center();
    background-color: #fff;
    cursor: pointer;
    box-shadow: -8px 0 12px #efefef;
    color: #000;
    text-shadow: 0 0 1px #000;
    i{ font-size: 32px;}
    &:hover{color: var(--pc-default-color)}
  }

  .collapse-btn{
    cursor: pointer;
    text-shadow: 0 0 1px #000;
    i{ font-size: 32px;}
    &:hover{color: var(--pc-default-color)}
  }

  .ctg-all-card{
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    z-index: 1003;
    box-shadow: 0 0 18px #cacaca;
    background-color: #fff;
    padding: 60px 30px;
    .header-box{
      width: 100%;
      padding: 0 40px 30px 40px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background-color: #fff;
      border-bottom: 1px solid #cacaca;
      margin-bottom: 20px;
      i{font-size: 36px}
    }
    .inner-ctg-card{
      background-color: #fff;
      display: flex;
      flex-wrap: wrap;
      .ctg-tag{
        line-height: 100px;
        margin: 0 40px;
        font-size: 28px;
        cursor: pointer;
      }
      .ctg-tag[select="1"]{
        font-weight: 600;
        color: var(--pc-default-color);
        text-shadow: 0 0 1px var(--pc-default-color);
      }
    }
  }
}
.tabs{
  display: flex;
  overflow-x: auto;
  border-bottom: 1px solid #efefef;
  background-color: #fff;
  padding-right: 180px;;

  &::-webkit-scrollbar{
    display: none;
  }
  .tab-item{
    text-align: center;
    transition: all .15s;
    
    cursor: pointer;
    height: 120px;
    line-height: 120px;
    white-space: nowrap;
    padding: 0 30px;
    &:first-child{
      .cd-time{
        background-color: var(--pc-default-color);
        color: #fff;
        padding: 10px;
        border-radius: 8px;
      }
    }
  }
  .tab-item[select="1"] {
    background-color: var(--pc-default-color);
    color: #fff;
    font-weight: 600;
  }
}

.listings-wrapper{
  background-color: #fff;
}
.listings{
  background-color: #f5f5f5;
  margin: 0 -20px;
  display: flex;
  flex-wrap: wrap;
  .listing-item{
    width: 20%;
    padding: 20px;
  }
}

.load-more{
  margin: 80px auto;
  border: 2px solid #efefef;
  box-shadow: 0 0 4px #666;
  text-align: center;
  cursor: pointer;
  height: 80px;
  @include center();
  width: 600px;
  color: #666;
  font-weight: 800;
  .loading{
    margin-right: 16px;
  }
}
</style>